import React from "react";
import { Database, Loader2, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InfoHint } from "@/components/InfoHint";

function isLive(status) {
  return status === "live" || status === "live-search";
}

const copy = {
  nl: {
    refreshing: "Metadata vernieuwen...",
    none: "Nog geen publieke metadata geladen. Lokale codebooks kunnen nog steeds gebruikt worden als er passende fragmenten zijn.",
    summary: (total, liveCount, curated) => <>We gebruiken <strong className="text-black">{total}</strong> publieke metadatabron{total === 1 ? "" : "nen"} als contextuele onderbouwing ({liveCount} live opgehaald, {curated} publieke fallback).</>,
    title: "Research metadata",
    automatic: "automatisch",
    help: "GooseBerry gebruikt publieke research metadata plus lokale project-codebooks om synthetische gebruikers te gronden. Dit paneel is informatief, je hoeft niets te selecteren. Druk op Vernieuwen om met je huidige context te zoeken.",
    grounding: <>Alleen gebruikt als <strong className="text-black">contextuele onderbouwing</strong>. Raw LISS Panel-data en datasets met beperkte toegang worden niet geanalyseerd.</>,
    localCodebooks: "Lokale codebooks",
    refresh: "Vernieuwen",
    show: (total) => `Toon metadata die gebruikt wordt (${total})`,
    live: "Live/publieke metadata",
    curated: "Publieke fallback metadata",
    open: "Open bron",
  },
  en: {
    refreshing: "Refreshing metadata...",
    none: "No public metadata loaded yet. Local codebooks can still be used when matching excerpts exist.",
    summary: (total, liveCount, curated) => <>We will use <strong className="text-black">{total}</strong> public metadata source{total === 1 ? "" : "s"} for grounding ({liveCount} fetched live, {curated} curated public fallback).</>,
    title: "Research metadata",
    automatic: "automatic",
    help: "GooseBerry uses public research metadata plus local project codebooks to ground synthetic users. This panel is informational, you do not need to select anything. Press Refresh to fetch with your current context.",
    grounding: <>Used as <strong className="text-black">grounding only</strong>. Raw LISS Panel data and permission-restricted datasets are not analyzed.</>,
    localCodebooks: "Local codebooks",
    refresh: "Refresh",
    show: (total) => `Show metadata that will be used (${total})`,
    live: "Live/public metadata",
    curated: "Curated public metadata",
    open: "Open source",
  },
};

function ResearchSummary({ loading, total, liveCount, language }) {
  const t = copy[language] || copy.en;
  if (loading) return <div className="text-sm text-black/70">{t.refreshing}</div>;
  if (total === 0) return <div className="text-sm text-black/70">{t.none}</div>;
  return <div className="text-sm text-black/70">{t.summary(total, liveCount, total - liveCount)}</div>;
}

export function ResearchMetadataPanel({ results, status = {}, loading, onRefresh, language = "en" }) {
  const t = copy[language] || copy.en;
  const liveCount = (results || []).filter((r) => isLive(r.sourceStatus)).length;
  const total = (results || []).length;
  const odisseiStatus = status.ODISSEI || "curated";

  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5" data-testid="synberry-research-card">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[rgba(97,203,163,0.18)] text-[#61CBA3]">
              <Database className="h-4 w-4" />
            </span>
            <div className="font-display text-lg font-semibold text-black">5. {t.title}</div>
            <span className="ml-1 text-[10px] uppercase tracking-wider text-black/45">{t.automatic}</span>
            <InfoHint testid="synberry-research-info">{t.help}</InfoHint>
          </div>
          <div className="mt-1 text-xs text-black/60">{t.grounding}</div>
        </div>
        <div className="flex items-center gap-1">
          <Badge className={`rounded-full border ${isLive(odisseiStatus) ? "border-[#61CBA3] bg-[rgba(97,203,163,0.22)]" : "border-black/15 bg-white"} text-black`}>
            ODISSEI: {odisseiStatus}
          </Badge>
          <Badge className="rounded-full border border-[#61CBA3] bg-[rgba(97,203,163,0.14)] text-black">
            {t.localCodebooks}
          </Badge>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <ResearchSummary loading={loading} total={total} liveCount={liveCount} language={language} />
        <Button variant="outline" size="sm" className="rounded-xl" onClick={onRefresh} disabled={loading} data-testid="synberry-research-refresh-button">
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          {t.refresh}
        </Button>
      </div>

      {total > 0 && (
        <details className="mt-3 group">
          <summary className="cursor-pointer text-xs text-[#61CBA3] hover:underline list-none">
            {t.show(total)}
          </summary>
          <div className="mt-2 space-y-2 max-h-[260px] overflow-y-auto pr-1" data-testid="synberry-research-results">
            {(results || []).map((r, i) => (
              <div key={`${r.url || r.title}-${i}`} className="rounded-xl border border-black/10 p-3" data-testid="synberry-research-result-card" aria-disabled="true">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="rounded-full border-[#61CBA3] text-[#61CBA3]">{r.source}</Badge>
                    <Badge className={`rounded-full border ${isLive(r.sourceStatus) ? "border-[#61CBA3] bg-[rgba(97,203,163,0.22)]" : "border-black/15 bg-white"} text-black`}>
                      {isLive(r.sourceStatus) ? t.live : t.curated}
                    </Badge>
                  </div>
                  {r.url && (
                    <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#61CBA3] underline underline-offset-4">
                      {t.open}
                    </a>
                  )}
                </div>
                <div className="mt-1 font-medium text-black text-sm">{r.title}</div>
                <div className="mt-0.5 text-xs text-black/70 line-clamp-3">{r.description}</div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
